import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { parseApiError } from '@saas-kit/lib';
import {
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  EmptyState,
  toast,
} from '@saas-kit/ui';
import { DataTable, type PaginationState } from '@saas-kit/ui/DataTable';

import {
  createInvoice,
  getSubscription,
  listInvoices,
  type AdminInvoice,
  type CreateInvoicePayload,
} from './api';
import { CreateInvoiceDialog } from './CreateInvoiceDialog';
import { InvoiceDetailDrawer } from './InvoiceDetailDrawer';
import { SubscriptionSummary } from './SubscriptionSummary';
import { useInvoiceColumns } from './useInvoiceColumns';

const DEFAULT_PAGE_SIZE = 25;

interface Props {
  tenantId: string;
  tenantName: string;
}

export function TenantBillingPanel({ tenantId, tenantName }: Props): JSX.Element {
  const { t } = useTranslation('adminBilling');
  const queryClient = useQueryClient();

  const [offset, setOffset] = useState(0);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [createOpen, setCreateOpen] = useState(false);
  const [activeInvoice, setActiveInvoice] = useState<AdminInvoice | null>(null);

  const subscriptionQuery = useQuery({
    queryKey: ['admin-billing-subscription', tenantId],
    queryFn: () => getSubscription(tenantId),
  });

  const invoicesQuery = useQuery({
    queryKey: ['admin-billing-invoices', tenantId, { offset, limit: pageSize }],
    queryFn: () => listInvoices(tenantId, { offset, limit: pageSize }),
    placeholderData: (prev) => prev,
  });
  const invoices = invoicesQuery.data?.items ?? [];
  const page = invoicesQuery.data?.page;

  const createMutation = useMutation({
    mutationFn: (payload: CreateInvoicePayload) => createInvoice(tenantId, payload),
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: ['admin-billing-invoices', tenantId],
      });
      toast.success(t('toasts.created'));
    },
    onError: (err) => toast.error(parseApiError(err).message),
  });

  const handlePick = useCallback(
    (invoice: AdminInvoice) => setActiveInvoice(invoice),
    [],
  );
  const columns = useInvoiceColumns({ onPick: handlePick });

  const total = page?.total ?? 0;
  const pagination: PaginationState = {
    pageIndex: Math.floor(offset / pageSize),
    pageSize,
    hasNext: page ? page.offset + page.limit < page.total : false,
    hasPrev: offset > 0,
    onNext: () => setOffset(offset + pageSize),
    onPrev: () => setOffset(Math.max(0, offset - pageSize)),
    onPageSizeChange: (n) => {
      setPageSize(n);
      setOffset(0);
    },
  };

  const showEmpty =
    !invoicesQuery.isLoading && !invoicesQuery.isError && total === 0;

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle>{t('subscription.heading')}</CardTitle>
        </CardHeader>
        <CardContent>
          <SubscriptionSummary
            subscription={subscriptionQuery.data ?? null}
            isLoading={subscriptionQuery.isLoading}
            isError={subscriptionQuery.isError}
          />
        </CardContent>
      </Card>

      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold">{t('invoices.heading')}</h2>
        <Button onClick={() => setCreateOpen(true)}>{t('actions.create')}</Button>
      </div>

      {showEmpty ? (
        <Card>
          <CardContent className="p-0">
            <EmptyState title={t('empty.title')} description={t('empty.body')} />
          </CardContent>
        </Card>
      ) : (
        <DataTable
          data={invoices}
          columns={columns}
          rowKey={(row) => row.id}
          testId="admin-tenant-invoices"
          selectable
          pagination={pagination}
          isLoading={invoicesQuery.isLoading}
          error={invoicesQuery.isError ? t('errors.invoices_failed') : null}
          exportable
          exportFilename="tenant-invoices"
        />
      )}

      <CreateInvoiceDialog
        open={createOpen}
        tenantName={tenantName}
        defaultCurrency="USD"
        isPending={createMutation.isPending}
        onOpenChange={setCreateOpen}
        onSubmit={(payload) =>
          createMutation.mutateAsync(payload).then(() => undefined)
        }
      />

      {activeInvoice ? (
        <InvoiceDetailDrawer
          tenantId={tenantId}
          invoice={activeInvoice}
          open={Boolean(activeInvoice)}
          onOpenChange={(o) => !o && setActiveInvoice(null)}
        />
      ) : null}
    </div>
  );
}
